import React, { useState, useEffect } from 'react';
import {
    getAllPunchCards,
    getPunchCardsByEmail,
    getPunchCardById,
    updatePunchCard,
    createPunchCard,
    deletePunchCardById
} from '../services/api';
import { useLocation } from 'react-router-dom';
import './PunchCardsDashboard.css'; // Custom CSS file for styling

const PunchCardsDashboard = () => {
    const [punchCards, setPunchCards] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredPunchCards, setFilteredPunchCards] = useState([]);
    const [errorMessage, setErrorMessage] = useState('');
    const [scannedCard, setScannedCard] = useState(null);
    const [newEmail, setNewEmail] = useState('');
    const [punchCardType, setPunchCardType] = useState('10');

    const location = useLocation();

    // Parse query parameters from the URL
    const queryParams = new URLSearchParams(location.search);
    const cardId = queryParams.get('id');

    useEffect(() => {
        if (cardId) {
            fetchPunchCardById(cardId);
        } else {
            fetchAllPunchCards();
        }
    }, [cardId]);

    const fetchAllPunchCards = async () => {
        try {
            const response = await getAllPunchCards();
            setPunchCards(response.data);
            setFilteredPunchCards(response.data);
        } catch (error) {
            console.error('Error fetching punch cards:', error);
            setErrorMessage('Failed to fetch punch cards.');
        }
    };

    const fetchPunchCardById = async (id) => {
        try {
            const response = await getPunchCardById(id);
            setScannedCard(response.data);
        } catch (error) {
            setErrorMessage('No punch card found for the provided ID.');
            console.error('Error fetching punch card by ID:', error);
        }
    };

    const handleSearch = async (e) => {
        e.preventDefault();
        setErrorMessage('');

        if (!searchQuery) {
            setErrorMessage('Please enter an email to search.');
            return;
        }

        try {
            const response = await getPunchCardsByEmail(searchQuery);
            setFilteredPunchCards(response.data);
        } catch (error) {
            setErrorMessage('No punch cards found for the provided email.');
            console.error(error);
        }
    };

    const handlePunchCard = async (card) => {
        if (card.remaining_punches <= 0) {
            alert('This card has no remaining punches.');
            return;
        }

        const updatedCard = {
            id: card.id,
            remaining_punches: card.remaining_punches - 1,
        };

        try {
            await updatePunchCard(updatedCard);
            if (cardId) {
                fetchPunchCardById(cardId);
            } else {
                fetchAllPunchCards();
            }
        } catch (error) {
            console.error('Error punching the card:', error);
            setErrorMessage('Failed to update punch card.');
        }
    };

    const handleCreatePunchCard = async () => {
        setErrorMessage('');
        if (!newEmail) {
            setErrorMessage('Please enter an email to create a punch card.');
            return;
        }

        try {
            await createPunchCard(newEmail, parseInt(punchCardType));
            fetchAllPunchCards();
            setNewEmail('');
            setPunchCardType('10');
        } catch (error) {
            console.error('Error creating punch card:', error);
            setErrorMessage('Failed to create punch card.');
        }
    };

    const handleDeletePunchCard = async (id) => {
        if (!window.confirm('Are you sure you want to delete this punch card?')) return;

        try {
            await deletePunchCardById(id);
            fetchAllPunchCards();
        } catch (error) {
            console.error('Error deleting punch card:', error);
            setErrorMessage('Failed to delete punch card.');
        }
    };

    return (
        <div className="container">
            <h2>Punch Cards Dashboard</h2>

            {cardId && scannedCard ? (
                <div>
                    <h3>Scanned Punch Card Details</h3>
                    <p>Email: {scannedCard.email}</p>
                    <p>Total Punches: {scannedCard.total_punches}</p>
                    <p>Remaining Punches: {scannedCard.remaining_punches}</p>
                    <button
                        disabled={scannedCard.remaining_punches <= 0}
                        onClick={() => handlePunchCard(scannedCard)}
                    >
                        Punch
                    </button>
                </div>
            ) : (
                <>
                    <h3>Create New Punch Card</h3>
                    <div style={{ marginBottom: '1rem' }}>
                        <input
                            type="email"
                            placeholder="Customer Email"
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                        />
                        <select
                            value={punchCardType}
                            onChange={(e) => setPunchCardType(e.target.value)}
                        >
                            <option value="5">5 Punches</option>
                            <option value="10">10 Punches</option>
                        </select>
                        <button onClick={handleCreatePunchCard}>Create</button>
                    </div>

                    <h3>All Punch Cards</h3>
                    {punchCards.length > 0 ? (
                        <table>
                            <thead>
                                <tr>
                                    <th>Email</th>
                                    <th>Total Punches</th>
                                    <th>Remaining Punches</th>
                                    <th>Created At</th>
                                    <th>Updated At</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredPunchCards.map((card) => (
                                    <tr key={card.id}>
                                        <td>{card.email}</td>
                                        <td>{card.total_punches}</td>
                                        <td>{card.remaining_punches}</td>
                                        <td>{new Date(card.created_at).toLocaleDateString()}</td>
                                        <td>{new Date(card.updated_at).toLocaleDateString()}</td>
                                        <td>
                                            <button
                                                disabled={card.remaining_punches <= 0}
                                                onClick={() => handlePunchCard(card)}
                                            >
                                                Punch
                                            </button>
                                            <button
                                                onClick={() => handleDeletePunchCard(card.id)}
                                                style={{ marginLeft: '0.5rem', color: 'red' }}
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <p>No punch cards found.</p>
                    )}

                    <h3>Search Punch Cards by Email</h3>
                    <form onSubmit={handleSearch}>
                        <input
                            type="email"
                            placeholder="Enter email"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        <button type="submit">Search</button>
                    </form>
                </>
            )}

            {errorMessage && <p style={{ color: 'red' }}>{errorMessage}</p>}
        </div>
    );
};

export default PunchCardsDashboard;
